/**
 * HOA Meetings & AGM Management Component
 * Handles meeting creation, agenda, attendance, minutes, and action items
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Calendar, Clock, Users, FileText, Plus, Play, Square, Download,
  CheckCircle, AlertCircle, MessageSquare, Vote, ClipboardList,
  UserPlus, Edit, Trash2, ChevronDown, ChevronRight, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOAMeetings = ({ orgId, owners = [] }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showMinuteEntry, setShowMinuteEntry] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [showActionItem, setShowActionItem] = useState(false);
  const [showMotion, setShowMotion] = useState(false);
  const [showProxy, setShowProxy] = useState(false);
  const [activeView, setActiveView] = useState('list');

  const [newMeeting, setNewMeeting] = useState({
    title: '', meeting_type: 'regular', date: '', start_time: '', end_time: '',
    location: '', virtual_link: '', quorum_required: 50, agenda_items: []
  });
  const [agendaInput, setAgendaInput] = useState('');
  const [minuteEntry, setMinuteEntry] = useState({ type: 'discussion', content: '', speaker: '' });
  const [attendeeForm, setAttendeeForm] = useState({ owner_name: '', unit: '', type: 'in_person' });
  const [actionForm, setActionForm] = useState({ description: '', assigned_to: '', due_date: '', priority: 'medium' });
  const [motionForm, setMotionForm] = useState({ title: '', description: '', proposed_by: '', seconded_by: '' });
  const [proxyForm, setProxyForm] = useState({ owner_name: '', unit: '', proxy_holder_name: '', method: 'online' });

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => { fetchMeetings(); }, [orgId]);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/hoa/meetings?organization_id=${orgId || ''}`, { headers: getHeaders() });
      setMeetings(res.data.meetings || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const createMeeting = async () => {
    try {
      const res = await api.post(`${API}/hoa/meetings`, {
        ...newMeeting, organization_id: orgId,
        agenda_items: newMeeting.agenda_items.map(a => ({ title: a }))
      }, { headers: getHeaders() });
      toast.success('Meeting created');
      setShowCreate(false);
      setNewMeeting({ title: '', meeting_type: 'regular', date: '', start_time: '', end_time: '', location: '', virtual_link: '', quorum_required: 50, agenda_items: [] });
      fetchMeetings();
    } catch (e) { toast.error('Failed to create meeting'); }
  };

  const startMeeting = async (id) => {
    try {
      await api.post(`${API}/hoa/meetings/${id}/start`, {}, { headers: getHeaders() });
      toast.success('Meeting started');
      fetchMeetings();
      if (selectedMeeting?.id === id) refreshSelected(id);
    } catch (e) { toast.error('Failed to start meeting'); }
  };

  const endMeeting = async (id) => {
    try {
      await api.post(`${API}/hoa/meetings/${id}/end`, {}, { headers: getHeaders() });
      toast.success('Meeting ended & minutes finalized');
      fetchMeetings();
      if (selectedMeeting?.id === id) refreshSelected(id);
    } catch (e) { toast.error('Failed to end meeting'); }
  };

  const refreshSelected = async (id) => {
    try {
      const res = await api.get(`${API}/hoa/meetings/${id}`, { headers: getHeaders() });
      setSelectedMeeting(res.data.meeting);
    } catch (e) { console.error(e); }
  };

  const addMinuteEntry = async () => {
    if (!minuteEntry.content) return;
    try {
      await api.post(`${API}/hoa/meetings/${selectedMeeting.id}/minutes`, minuteEntry, { headers: getHeaders() });
      toast.success('Minute entry added');
      setMinuteEntry({ type: 'discussion', content: '', speaker: '' });
      setShowMinuteEntry(false);
      refreshSelected(selectedMeeting.id);
    } catch (e) { toast.error('Failed to add entry'); }
  };

  const addAttendee = async () => {
    try {
      const res = await api.post(`${API}/hoa/meetings/${selectedMeeting.id}/attendance`, attendeeForm, { headers: getHeaders() });
      toast.success(`${attendeeForm.owner_name} checked in. Quorum: ${res.data.quorum?.percentage}%${res.data.quorum?.met ? ' (MET)' : ''}`);
      setAttendeeForm({ owner_name: '', unit: '', type: 'in_person' });
      setShowAttendance(false);
      refreshSelected(selectedMeeting.id);
    } catch (e) { toast.error('Failed to record attendance'); }
  };

  const addActionItem = async () => {
    try {
      await api.post(`${API}/hoa/meetings/${selectedMeeting.id}/action-items`, actionForm, { headers: getHeaders() });
      toast.success('Action item added');
      setActionForm({ description: '', assigned_to: '', due_date: '', priority: 'medium' });
      setShowActionItem(false);
      refreshSelected(selectedMeeting.id);
    } catch (e) { toast.error('Failed to add action item'); }
  };

  const createMotion = async () => {
    try {
      await api.post(`${API}/hoa/meetings/${selectedMeeting.id}/motions`, motionForm, { headers: getHeaders() });
      toast.success('Motion created');
      setMotionForm({ title: '', description: '', proposed_by: '', seconded_by: '' });
      setShowMotion(false);
      refreshSelected(selectedMeeting.id);
    } catch (e) { toast.error('Failed to create motion'); }
  };

  const castVote = async (motionId, vote) => {
    try {
      await api.post(`${API}/hoa/meetings/${selectedMeeting.id}/motions/${motionId}/vote`, { vote }, { headers: getHeaders() });
      toast.success(`Vote: ${vote.toUpperCase()}`);
      refreshSelected(selectedMeeting.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Vote failed'); }
  };

  const closeMotion = async (motionId) => {
    try {
      const res = await api.post(`${API}/hoa/meetings/${selectedMeeting.id}/motions/${motionId}/close`, {}, { headers: getHeaders() });
      const r = res.data.results;
      toast.success(`Motion ${r.passed ? 'PASSED' : 'FAILED'}: ${r.yes} Yes, ${r.no} No, ${r.abstain} Abstain`);
      refreshSelected(selectedMeeting.id);
    } catch (e) { toast.error('Failed to close motion'); }
  };

  const assignProxy = async () => {
    try {
      await api.post(`${API}/hoa/meetings/${selectedMeeting.id}/proxy`, proxyForm, { headers: getHeaders() });
      toast.success('Proxy assigned');
      setProxyForm({ owner_name: '', unit: '', proxy_holder_name: '', method: 'online' });
      setShowProxy(false);
      refreshSelected(selectedMeeting.id);
    } catch (e) { toast.error('Failed to assign proxy'); }
  };

  const exportMinutes = async (meetingId) => {
    try {
      const res = await api.get(`${API}/hoa/meetings/${meetingId}/minutes-export`, {
        headers: getHeaders(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `meeting_minutes_${meetingId}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Minutes exported');
    } catch (e) { toast.error('Export failed'); }
  };

  const statusBadge = (status) => {
    const map = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={`${map[status] || 'bg-gray-100 text-gray-800'} text-xs`}>{status?.replace('_', ' ')}</Badge>;
  };

  const typeLabel = (t) => ({ regular: 'Board Meeting', agm: 'Annual General Meeting', special: 'Special Meeting', emergency: 'Emergency Meeting' }[t] || t);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;

  // Meeting Detail View
  if (selectedMeeting) {
    const m = selectedMeeting;
    const attendees = m.attendees || [];
    const minutes = m.minutes || [];
    const motions = m.motions || [];
    const actionItems = m.action_items || [];
    const proxies = m.proxies || [];

    return (
      <div className="space-y-4" data-testid="meeting-detail">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <Button variant="ghost" size="sm" onClick={() => setSelectedMeeting(null)} className="mb-1 text-xs">
              &larr; Back to Meetings
            </Button>
            <h3 className="text-lg sm:text-xl font-bold">{m.title}</h3>
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600 mt-1">
              <span className="flex items-center"><Calendar className="h-3 w-3 mr-1" />{m.date}</span>
              <span className="flex items-center"><Clock className="h-3 w-3 mr-1" />{m.start_time} - {m.end_time}</span>
              {statusBadge(m.status)}
              <Badge variant="outline" className="text-xs">{typeLabel(m.meeting_type)}</Badge>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {m.status === 'scheduled' && (
              <Button size="sm" onClick={() => startMeeting(m.id)} className="bg-green-600 hover:bg-green-700 text-xs" data-testid="start-meeting-btn">
                <Play className="h-3 w-3 mr-1" /> Start Meeting
              </Button>
            )}
            {m.status === 'in_progress' && (
              <Button size="sm" onClick={() => endMeeting(m.id)} className="bg-red-600 hover:bg-red-700 text-xs" data-testid="end-meeting-btn">
                <Square className="h-3 w-3 mr-1" /> End Meeting
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => exportMinutes(m.id)} className="text-xs" data-testid="export-minutes-btn">
              <Download className="h-3 w-3 mr-1" /> Export Minutes
            </Button>
          </div>
        </div>

        {/* Agenda */}
        {m.agenda_items?.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center"><ClipboardList className="h-4 w-4 mr-2 text-blue-600" />Agenda</CardTitle></CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-1 text-sm">
                {m.agenda_items.map((a, i) => <li key={i}>{typeof a === 'object' ? a.title : a}</li>)}
              </ol>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="attendance">
          <div className="overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex w-auto min-w-full sm:grid sm:grid-cols-5">
              <TabsTrigger value="attendance" className="text-xs whitespace-nowrap">
                <Users className="h-3 w-3 mr-1" />Attendance ({attendees.length})
              </TabsTrigger>
              <TabsTrigger value="minutes" className="text-xs whitespace-nowrap">
                <FileText className="h-3 w-3 mr-1" />Minutes ({minutes.length})
              </TabsTrigger>
              <TabsTrigger value="motions" className="text-xs whitespace-nowrap">
                <Vote className="h-3 w-3 mr-1" />Motions ({motions.length})
              </TabsTrigger>
              <TabsTrigger value="actions" className="text-xs whitespace-nowrap">
                <CheckCircle className="h-3 w-3 mr-1" />Actions ({actionItems.length})
              </TabsTrigger>
              <TabsTrigger value="proxies" className="text-xs whitespace-nowrap">
                <UserPlus className="h-3 w-3 mr-1" />Proxies ({proxies.length})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Attendance Tab */}
          <TabsContent value="attendance">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Attendance Register</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowAttendance(true)} className="text-xs" data-testid="add-attendee-btn">
                  <UserPlus className="h-3 w-3 mr-1" /> Check In
                </Button>
              </CardHeader>
              <CardContent>
                {attendees.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No attendees checked in yet</p>
                ) : (
                  <div className="space-y-2">
                    {attendees.map((a, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <div>
                          <span className="font-medium">{a.owner_name}</span>
                          {a.unit && <span className="text-gray-500 ml-2">Unit {a.unit}</span>}
                        </div>
                        <Badge className={`text-xs ${a.type === 'in_person' ? 'bg-green-100 text-green-800' : a.type === 'virtual' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                          {a.type?.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Minutes Tab */}
          <TabsContent value="minutes">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Meeting Minutes</CardTitle>
                {m.status === 'in_progress' && (
                  <Button size="sm" variant="outline" onClick={() => setShowMinuteEntry(true)} className="text-xs" data-testid="add-minute-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Entry
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {minutes.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No minutes recorded yet</p>
                ) : (
                  <div className="space-y-2">
                    {minutes.map((entry, i) => (
                      <div key={i} className="p-2 border rounded text-sm">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[10px]">{entry.type}</Badge>
                          {entry.speaker && <span className="text-gray-500 text-xs">Speaker: {entry.speaker}</span>}
                          <span className="text-gray-400 text-[10px]">{entry.timestamp?.slice(11, 16)}</span>
                        </div>
                        <p className={`${entry.type === 'motion' || entry.type === 'resolution' ? 'font-semibold' : ''}`}>
                          {entry.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Motions/Voting Tab */}
          <TabsContent value="motions">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Motions & Voting</CardTitle>
                {m.status === 'in_progress' && (
                  <Button size="sm" variant="outline" onClick={() => setShowMotion(true)} className="text-xs" data-testid="create-motion-btn">
                    <Plus className="h-3 w-3 mr-1" /> New Motion
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {motions.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No motions created yet</p>
                ) : (
                  <div className="space-y-3">
                    {motions.map((motion) => (
                      <div key={motion.id} className="border rounded-lg p-3" data-testid={`motion-${motion.id}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <div>
                            <h4 className="font-semibold text-sm">{motion.title}</h4>
                            <p className="text-xs text-gray-500">Proposed: {motion.proposed_by} {motion.seconded_by ? `| Seconded: ${motion.seconded_by}` : ''}</p>
                          </div>
                          <Badge className={`text-xs ${motion.status === 'passed' ? 'bg-green-100 text-green-800' : motion.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {motion.status}
                          </Badge>
                        </div>
                        {motion.description && <p className="text-sm text-gray-600 mb-2">{motion.description}</p>}
                        
                        {/* Vote Results */}
                        {motion.results && (
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <div className="bg-green-50 p-2 rounded text-center">
                              <p className="text-lg font-bold text-green-600">{motion.results.yes}</p>
                              <p className="text-[10px] text-green-600">YES</p>
                            </div>
                            <div className="bg-red-50 p-2 rounded text-center">
                              <p className="text-lg font-bold text-red-600">{motion.results.no}</p>
                              <p className="text-[10px] text-red-600">NO</p>
                            </div>
                            <div className="bg-gray-50 p-2 rounded text-center">
                              <p className="text-lg font-bold text-gray-600">{motion.results.abstain}</p>
                              <p className="text-[10px] text-gray-600">ABSTAIN</p>
                            </div>
                          </div>
                        )}

                        {/* Voting Buttons */}
                        {motion.status === 'open' && (
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs flex-1" onClick={() => castVote(motion.id, 'yes')} data-testid={`vote-yes-${motion.id}`}>
                              Yes
                            </Button>
                            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-xs flex-1" onClick={() => castVote(motion.id, 'no')} data-testid={`vote-no-${motion.id}`}>
                              No
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs flex-1" onClick={() => castVote(motion.id, 'abstain')}>
                              Abstain
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => closeMotion(motion.id)} data-testid={`close-vote-${motion.id}`}>
                              Close Vote
                            </Button>
                          </div>
                        )}

                        {/* Vote Audit Trail */}
                        {motion.votes?.length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-gray-500 cursor-pointer">Audit Trail ({motion.votes.length} votes)</summary>
                            <div className="mt-1 space-y-1">
                              {motion.votes.map((v, vi) => (
                                <div key={vi} className="text-xs flex items-center gap-2 py-0.5">
                                  <span className={`w-1.5 h-1.5 rounded-full ${v.vote === 'yes' ? 'bg-green-500' : v.vote === 'no' ? 'bg-red-500' : 'bg-gray-400'}`} />
                                  <span>{v.voter_name || 'Anonymous'}</span>
                                  <span className="font-medium">{v.vote}</span>
                                  {v.is_proxy && <Badge className="text-[10px] bg-purple-100 text-purple-700">Proxy</Badge>}
                                  <span className="text-gray-400">{v.cast_at?.slice(11, 16)}</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Action Items Tab */}
          <TabsContent value="actions">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Action Items</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowActionItem(true)} className="text-xs" data-testid="add-action-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add Item
                </Button>
              </CardHeader>
              <CardContent>
                {actionItems.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No action items yet</p>
                ) : (
                  <div className="space-y-2">
                    {actionItems.map((item, i) => (
                      <div key={i} className="p-2 border rounded flex items-start justify-between text-sm">
                        <div>
                          <p className="font-medium">{item.description}</p>
                          <p className="text-xs text-gray-500">Assigned: {item.assigned_to || 'TBD'} | Due: {item.due_date || 'TBD'}</p>
                        </div>
                        <Badge className={`text-xs shrink-0 ml-2 ${item.priority === 'high' ? 'bg-red-100 text-red-700' : item.priority === 'low' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {item.priority}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Proxies Tab */}
          <TabsContent value="proxies">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Proxy Assignments</CardTitle>
                <Button size="sm" variant="outline" onClick={() => setShowProxy(true)} className="text-xs" data-testid="assign-proxy-btn">
                  <UserPlus className="h-3 w-3 mr-1" /> Assign Proxy
                </Button>
              </CardHeader>
              <CardContent>
                {proxies.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No proxy assignments</p>
                ) : (
                  <div className="space-y-2">
                    {proxies.map((p, i) => (
                      <div key={i} className="p-2 bg-purple-50 rounded text-sm flex items-center justify-between">
                        <div>
                          <p><span className="font-medium">{p.owner_name}</span> (Unit {p.unit})</p>
                          <p className="text-xs text-gray-600">Proxy to: <strong>{p.proxy_holder_name}</strong></p>
                        </div>
                        <Badge variant="outline" className="text-xs">{p.assigned_method}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialogs */}
        {/* Add Attendee */}
        <Dialog open={showAttendance} onOpenChange={setShowAttendance}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-base">Check In Attendee</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={attendeeForm.owner_name} onChange={e => setAttendeeForm({...attendeeForm, owner_name: e.target.value})} placeholder="Owner name" /></div>
              <div><Label className="text-xs">Unit</Label><Input value={attendeeForm.unit} onChange={e => setAttendeeForm({...attendeeForm, unit: e.target.value})} placeholder="Unit number" /></div>
              <div><Label className="text-xs">Type</Label>
                <Select value={attendeeForm.type} onValueChange={v => setAttendeeForm({...attendeeForm, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">In Person</SelectItem>
                    <SelectItem value="virtual">Virtual</SelectItem>
                    <SelectItem value="proxy">Proxy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button size="sm" onClick={addAttendee}>Check In</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Minute Entry */}
        <Dialog open={showMinuteEntry} onOpenChange={setShowMinuteEntry}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-base">Add Minute Entry</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Type</Label>
                <Select value={minuteEntry.type} onValueChange={v => setMinuteEntry({...minuteEntry, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discussion">Discussion</SelectItem>
                    <SelectItem value="motion">Motion</SelectItem>
                    <SelectItem value="resolution">Resolution</SelectItem>
                    <SelectItem value="action_item">Action Item</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Speaker</Label><Input value={minuteEntry.speaker} onChange={e => setMinuteEntry({...minuteEntry, speaker: e.target.value})} placeholder="Speaker name (optional)" /></div>
              <div><Label className="text-xs">Content</Label><textarea className="w-full border rounded p-2 text-sm min-h-[80px]" value={minuteEntry.content} onChange={e => setMinuteEntry({...minuteEntry, content: e.target.value})} placeholder="What was discussed..." /></div>
            </div>
            <DialogFooter><Button size="sm" onClick={addMinuteEntry}>Add Entry</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Motion */}
        <Dialog open={showMotion} onOpenChange={setShowMotion}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-base">Create Motion</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Motion Title</Label><Input value={motionForm.title} onChange={e => setMotionForm({...motionForm, title: e.target.value})} placeholder="e.g., Approve roof repair budget" /></div>
              <div><Label className="text-xs">Description</Label><textarea className="w-full border rounded p-2 text-sm min-h-[60px]" value={motionForm.description} onChange={e => setMotionForm({...motionForm, description: e.target.value})} /></div>
              <div><Label className="text-xs">Proposed By</Label><Input value={motionForm.proposed_by} onChange={e => setMotionForm({...motionForm, proposed_by: e.target.value})} /></div>
              <div><Label className="text-xs">Seconded By</Label><Input value={motionForm.seconded_by} onChange={e => setMotionForm({...motionForm, seconded_by: e.target.value})} /></div>
            </div>
            <DialogFooter><Button size="sm" onClick={createMotion}>Create Motion</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Action Item */}
        <Dialog open={showActionItem} onOpenChange={setShowActionItem}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-base">Add Action Item</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Description</Label><Input value={actionForm.description} onChange={e => setActionForm({...actionForm, description: e.target.value})} /></div>
              <div><Label className="text-xs">Assigned To</Label><Input value={actionForm.assigned_to} onChange={e => setActionForm({...actionForm, assigned_to: e.target.value})} /></div>
              <div><Label className="text-xs">Due Date</Label><Input type="date" value={actionForm.due_date} onChange={e => setActionForm({...actionForm, due_date: e.target.value})} /></div>
              <div><Label className="text-xs">Priority</Label>
                <Select value={actionForm.priority} onValueChange={v => setActionForm({...actionForm, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button size="sm" onClick={addActionItem}>Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Proxy */}
        <Dialog open={showProxy} onOpenChange={setShowProxy}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-base">Assign Proxy</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Owner Name (Absent)</Label><Input value={proxyForm.owner_name} onChange={e => setProxyForm({...proxyForm, owner_name: e.target.value})} /></div>
              <div><Label className="text-xs">Unit</Label><Input value={proxyForm.unit} onChange={e => setProxyForm({...proxyForm, unit: e.target.value})} /></div>
              <div><Label className="text-xs">Proxy Holder Name</Label><Input value={proxyForm.proxy_holder_name} onChange={e => setProxyForm({...proxyForm, proxy_holder_name: e.target.value})} /></div>
              <div><Label className="text-xs">Method</Label>
                <Select value={proxyForm.method} onValueChange={v => setProxyForm({...proxyForm, method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Online (before meeting)</SelectItem>
                    <SelectItem value="in_person">In-Person (at meeting)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button size="sm" onClick={assignProxy}>Assign Proxy</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Meeting List View
  return (
    <div className="space-y-4" data-testid="meetings-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold flex items-center"><Calendar className="h-5 w-5 mr-2 text-blue-600" />Meetings & AGM</h3>
          <p className="text-xs text-gray-500">Manage board meetings, AGMs, voting, and minutes</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-meeting-btn">
          <Plus className="h-4 w-4 mr-1" /> New Meeting
        </Button>
      </div>

      {meetings.length === 0 ? (
        <Card><CardContent className="text-center py-8 text-sm text-gray-500">No meetings scheduled. Create your first meeting to get started.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {meetings.map(m => (
            <Card key={m.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSelectedMeeting(m); refreshSelected(m.id); }} data-testid={`meeting-card-${m.id}`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm">{m.title}</h4>
                      {statusBadge(m.status)}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center"><Calendar className="h-3 w-3 mr-1" />{m.date}</span>
                      <span className="flex items-center"><Clock className="h-3 w-3 mr-1" />{m.start_time}</span>
                      <span className="flex items-center"><Users className="h-3 w-3 mr-1" />{m.attendees?.length || 0} attendees</span>
                      <Badge variant="outline" className="text-[10px]">{typeLabel(m.meeting_type)}</Badge>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 hidden sm:block" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Meeting Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-base">Schedule New Meeting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Meeting Title</Label><Input value={newMeeting.title} onChange={e => setNewMeeting({...newMeeting, title: e.target.value})} placeholder="e.g., Q1 2026 Board Meeting" /></div>
            <div><Label className="text-xs">Meeting Type</Label>
              <Select value={newMeeting.meeting_type} onValueChange={v => setNewMeeting({...newMeeting, meeting_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular Board Meeting</SelectItem>
                  <SelectItem value="agm">Annual General Meeting (AGM)</SelectItem>
                  <SelectItem value="special">Special Meeting</SelectItem>
                  <SelectItem value="emergency">Emergency Meeting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Date</Label><Input type="date" value={newMeeting.date} onChange={e => setNewMeeting({...newMeeting, date: e.target.value})} /></div>
              <div><Label className="text-xs">Quorum %</Label><Input type="number" value={newMeeting.quorum_required} onChange={e => setNewMeeting({...newMeeting, quorum_required: parseInt(e.target.value)})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Start Time</Label><Input type="time" value={newMeeting.start_time} onChange={e => setNewMeeting({...newMeeting, start_time: e.target.value})} /></div>
              <div><Label className="text-xs">End Time</Label><Input type="time" value={newMeeting.end_time} onChange={e => setNewMeeting({...newMeeting, end_time: e.target.value})} /></div>
            </div>
            <div><Label className="text-xs">Location</Label><Input value={newMeeting.location} onChange={e => setNewMeeting({...newMeeting, location: e.target.value})} placeholder="Community Hall, Room 201" /></div>
            <div><Label className="text-xs">Virtual Meeting Link (optional)</Label><Input value={newMeeting.virtual_link} onChange={e => setNewMeeting({...newMeeting, virtual_link: e.target.value})} placeholder="https://zoom.us/..." /></div>
            <div>
              <Label className="text-xs">Agenda Items</Label>
              <div className="flex gap-2">
                <Input value={agendaInput} onChange={e => setAgendaInput(e.target.value)} placeholder="Add agenda item" onKeyDown={e => { if (e.key === 'Enter' && agendaInput) { setNewMeeting({...newMeeting, agenda_items: [...newMeeting.agenda_items, agendaInput]}); setAgendaInput(''); }}} />
                <Button size="sm" variant="outline" onClick={() => { if (agendaInput) { setNewMeeting({...newMeeting, agenda_items: [...newMeeting.agenda_items, agendaInput]}); setAgendaInput(''); }}}>Add</Button>
              </div>
              {newMeeting.agenda_items.length > 0 && (
                <ol className="list-decimal list-inside mt-2 text-sm space-y-1">
                  {newMeeting.agenda_items.map((a, i) => (
                    <li key={i} className="flex items-center justify-between">
                      <span>{a}</span>
                      <button className="text-red-400 hover:text-red-600 text-xs" onClick={() => setNewMeeting({...newMeeting, agenda_items: newMeeting.agenda_items.filter((_, j) => j !== i)})}>Remove</button>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
          <DialogFooter><Button size="sm" onClick={createMeeting} disabled={!newMeeting.title || !newMeeting.date}>Create Meeting</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HOAMeetings;
